import * as React from 'react';
import { ASK_MAX_QUESTION_CHARS, askRoom, describeAskAnswer, type AskRoomAnswer } from '../api/ask';
import type { RoomDocument } from '../api/types';
import { noteGuideActivity } from '../lib/first-run-guide';
import { cn } from '../lib/utils';
import { Button } from './button';
import { CardTitle } from './card';
import { ErrorText } from './error-text';
import { Icon } from './icons';
import { Input } from './input';

/** Where an "Ask the Data Room" question stands. `question` is the text as it
 * was asked (the prompt stays editable; the answer keeps what was asked). */
export type AskState =
  | { status: 'idle' }
  | { status: 'loading'; question: string }
  | { status: 'answered'; question: string; answer: AskRoomAnswer }
  | { status: 'error'; question: string; error: unknown };

export interface AskTheRoomController {
  /** The prompt's text. */
  question: string;
  setQuestion: (question: string) => void;
  state: AskState;
  /** Ask the prompt's text (a blank prompt does nothing). */
  ask: () => void;
  /** Drop the answer (and ignore one still on its way); the prompt's text stays. */
  clear: () => void;
  /** Drop the answer AND the prompt's text — picking a folder starts over (owner, 2026-10-22). */
  reset: () => void;
}

/**
 * "Ask the Data Room" (ADR-0035) as page state, so the page decides where the
 * answer goes (owner, 2026-10-15: the Data Room's one results area shows the
 * Ask answer OR the folder's documents — whichever the person did last).
 * `onAsk` runs as a question is asked: the page clears the folder there.
 * Only the latest question's answer is kept (an earlier slow answer is dropped).
 */
export function useAskTheRoom(roomId: string, onAsk?: () => void): AskTheRoomController {
  const [question, setQuestion] = React.useState('');
  const [state, setState] = React.useState<AskState>({ status: 'idle' });
  const latest = React.useRef(0);

  const ask = () => {
    const q = question.trim();
    if (!q) return;
    const ticket = ++latest.current;
    noteGuideActivity('ask');
    onAsk?.();
    setState({ status: 'loading', question: q });
    askRoom(roomId, q).then(
      (answer) => {
        if (ticket === latest.current) setState({ status: 'answered', question: q, answer });
      },
      (error: unknown) => {
        if (ticket === latest.current) setState({ status: 'error', question: q, error });
      },
    );
  };
  // Stable, so a page can call it from an effect.
  const clear = React.useCallback(() => {
    latest.current += 1;
    setState((s) => (s.status === 'idle' ? s : { status: 'idle' }));
  }, []);
  const reset = React.useCallback(() => {
    latest.current += 1;
    setQuestion('');
    setState((s) => (s.status === 'idle' ? s : { status: 'idle' }));
  }, []);
  return { question, setQuestion, state, ask, clear, reset };
}

/** The documents an answer found ([] while loading, on error, or for a
 * not-a-find answer). */
export function askAnswerDocuments(state: AskState): RoomDocument[] {
  return state.status === 'answered' && state.answer.kind === 'documents'
    ? state.answer.results
    : [];
}

/** The "Ask the Data Room" prompt: the question box and Ask. The answer is
 * shown by the page, in its results area (`AskAnswerSummary` + its own rows). */
export function AskTheRoomPrompt({
  ask,
  className,
}: {
  ask: AskTheRoomController;
  className?: string;
}) {
  const loading = ask.state.status === 'loading';
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        ask.ask();
      }}
      className={cn('flex gap-2', className)}
      role="search"
      aria-label="Ask the Data Room"
    >
      <Input
        aria-label="Ask the Data Room"
        placeholder='Ask the Data Room, e.g. "Show me all of the P&Ls"'
        value={ask.question}
        maxLength={ASK_MAX_QUESTION_CHARS}
        onChange={(e) => ask.setQuestion(e.target.value)}
      />
      <Button type="submit" disabled={!ask.question.trim() || loading}>
        {loading ? 'Asking…' : 'Ask'}
      </Button>
    </form>
  );
}

/**
 * The line above an Ask answer's rows: "Asking…", the error, the server's one
 * fixed message for a question that asks for an opinion, or how many documents
 * were found and how the question was read ("2 documents — Financial
 * Performance › Profit and Loss Statements · latest only").
 */
export function AskAnswerSummary({ state }: { state: AskState }) {
  if (state.status === 'idle') return null;
  if (state.status === 'loading') {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        Asking…
      </p>
    );
  }
  if (state.status === 'error') return <ErrorText error={state.error} />;
  const { answer } = state;
  if (answer.kind === 'not_a_find_request') {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {answer.message}
      </p>
    );
  }
  const n = answer.results.length;
  return (
    <p className="text-sm text-muted-foreground" role="status">
      {n === 0
        ? 'No matching documents found.'
        : `${n} document${n === 1 ? '' : 's'}${answer.truncated ? ' (first results)' : ''} — ${describeAskAnswer(answer)}`}
    </p>
  );
}

/**
 * The results area's title — what it shows: "Answer: …", "Folder: …", "All
 * documents" — with a ✕ that returns to All documents whenever it shows
 * anything else (`onClear` absent → no ✕).
 */
export function ResultsTitle({ title, onClear }: { title: string; onClear?: () => void }) {
  return (
    <div className="flex min-w-0 items-start gap-1">
      <CardTitle className="min-w-0 break-words">{title}</CardTitle>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear and show all documents"
          title="Show all documents"
          className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-lg:min-h-tap max-lg:min-w-tap"
        >
          <Icon name="close" size={16} />
        </button>
      ) : null}
    </div>
  );
}
